interface BrandColors {
  primary: string;
  primaryHover: string;
  primaryLight: string;
  primaryDark: string;
  secondary: string;
  secondaryHover: string;
  secondaryLight: string;
  accent: string;
  accentHover: string;
  background: string;
  text: string;
  textSecondary: string;
  textMuted: string;
  success: string;
  warning: string;
  error: string;
  info: string;
}

interface PWAComponentTheme {
  networkOnline: string;
  networkSlow: string;
  networkOffline: string;
  installPrompt: string;
  installPromptHover: string;
  mobileNav: string;
  mobileNavActive: string;
  statusIndicator: string;
  statusIndicatorText: string;
}

const createPWAComponentTheme = (colors: BrandColors): PWAComponentTheme => ({
  networkOnline: colors.success || adjustBrightness(colors.primary, -20),
  networkSlow: colors.warning || adjustBrightness(colors.accent, -10),
  networkOffline: colors.error || adjustBrightness(colors.primary, 20),
  installPrompt: colors.primary,
  installPromptHover: colors.primaryHover,
  mobileNav: colors.background,
  mobileNavActive: colors.primaryLight,
  statusIndicator: colors.primary,
  statusIndicatorText: colors.text
});

export const applyBrandColorsToElements = (colors: BrandColors) => {
  // Apply brand colors to CSS custom properties and specific UI elements
  const root = document.documentElement;
  
  // Set CSS custom properties for dynamic theming
  root.style.setProperty('--brand-primary', colors.primary);
  root.style.setProperty('--brand-primary-hover', colors.primaryHover);
  root.style.setProperty('--brand-primary-light', colors.primaryLight);
  root.style.setProperty('--brand-primary-dark', colors.primaryDark);
  root.style.setProperty('--brand-secondary', colors.secondary);
  root.style.setProperty('--brand-secondary-hover', colors.secondaryHover);
  root.style.setProperty('--brand-secondary-light', colors.secondaryLight);
  root.style.setProperty('--brand-accent', colors.accent);
  root.style.setProperty('--brand-accent-hover', colors.accentHover);
  root.style.setProperty('--brand-background', colors.background);
  root.style.setProperty('--brand-text', colors.text);
  root.style.setProperty('--brand-text-secondary', colors.textSecondary);
  root.style.setProperty('--brand-text-muted', colors.textMuted);
  root.style.setProperty('--brand-success', colors.success);
  root.style.setProperty('--brand-warning', colors.warning);
  root.style.setProperty('--brand-error', colors.error);
  root.style.setProperty('--brand-info', colors.info);
  
  // Generate PWA component specific colors
  const pwaTheme = createPWAComponentTheme(colors);
  root.style.setProperty('--pwa-network-online', pwaTheme.networkOnline);
  root.style.setProperty('--pwa-network-slow', pwaTheme.networkSlow);
  root.style.setProperty('--pwa-network-offline', pwaTheme.networkOffline);
  root.style.setProperty('--pwa-install-prompt', pwaTheme.installPrompt);
  root.style.setProperty('--pwa-install-prompt-hover', pwaTheme.installPromptHover);
  root.style.setProperty('--pwa-mobile-nav', pwaTheme.mobileNav);
  root.style.setProperty('--pwa-mobile-nav-active', pwaTheme.mobileNavActive);
  root.style.setProperty('--pwa-status-indicator', pwaTheme.statusIndicator);
  root.style.setProperty('--pwa-status-indicator-text', pwaTheme.statusIndicatorText);
  
  const style = document.createElement('style');
  style.id = 'brand-colors-override';
  
  // Remove existing brand color overrides
  const existing = document.getElementById('brand-colors-override');
  if (existing) {
    existing.remove();
  }
  
  style.textContent = `
    /* CSS Custom Properties for dynamic theming */
    :root {
      --brand-primary: ${colors.primary};
      --brand-primary-hover: ${colors.primaryHover};
      --brand-primary-light: ${colors.primaryLight};
      --brand-primary-dark: ${colors.primaryDark};
      --brand-secondary: ${colors.secondary};
      --brand-secondary-hover: ${colors.secondaryHover};
      --brand-secondary-light: ${colors.secondaryLight};
      --brand-accent: ${colors.accent};
      --brand-accent-hover: ${colors.accentHover};
      --brand-background: ${colors.background};
      --brand-text: ${colors.text};
      --brand-text-secondary: ${colors.textSecondary};
      --brand-text-muted: ${colors.textMuted};
      --brand-success: ${colors.success};
      --brand-warning: ${colors.warning};
      --brand-error: ${colors.error};
      --brand-info: ${colors.info};
      
      /* PWA Component Colors */
      --pwa-network-online: ${pwaTheme.networkOnline};
      --pwa-network-slow: ${pwaTheme.networkSlow};
      --pwa-network-offline: ${pwaTheme.networkOffline};
      --pwa-install-prompt: ${pwaTheme.installPrompt};
      --pwa-install-prompt-hover: ${pwaTheme.installPromptHover};
      --pwa-mobile-nav: ${pwaTheme.mobileNav};
      --pwa-mobile-nav-active: ${pwaTheme.mobileNavActive};
      --pwa-status-indicator: ${pwaTheme.statusIndicator};
      --pwa-status-indicator-text: ${pwaTheme.statusIndicatorText};
    }
    
    /* Primary brand color applications */
    .bg-blue-600, .brand-primary { background-color: var(--brand-primary) !important; }
    .hover\\:bg-blue-700:hover, .brand-primary:hover { background-color: var(--brand-primary-hover) !important; }
    .text-blue-600, .brand-text-primary { color: var(--brand-primary) !important; }
    .border-blue-500, .brand-border-primary { border-color: var(--brand-primary) !important; }
    .ring-blue-500 { --tw-ring-color: var(--brand-primary) !important; }
    .focus\\:ring-blue-500:focus { --tw-ring-color: var(--brand-primary) !important; }
    
    /* Secondary brand color applications */
    .bg-green-600, .brand-secondary { background-color: var(--brand-secondary) !important; }
    .hover\\:bg-green-700:hover, .brand-secondary:hover { background-color: var(--brand-secondary-hover) !important; }
    .text-green-600, .brand-text-secondary { color: var(--brand-secondary) !important; }
    
    /* Accent brand color applications */
    .bg-yellow-600, .brand-accent { background-color: var(--brand-accent) !important; }
    .text-yellow-600, .brand-text-accent { color: var(--brand-accent) !important; }
    .hover\\:bg-yellow-700:hover, .brand-accent:hover { background-color: var(--brand-accent-hover) !important; }
    
    /* PWA Component Styles */
    .pwa-network-online { background-color: var(--pwa-network-online) !important; }
    .pwa-network-slow { background-color: var(--pwa-network-slow) !important; }
    .pwa-network-offline { background-color: var(--pwa-network-offline) !important; }
    .pwa-install-prompt { background-color: var(--pwa-install-prompt) !important; }
    .pwa-install-prompt:hover { background-color: var(--pwa-install-prompt-hover) !important; }
    .pwa-mobile-nav { background-color: var(--pwa-mobile-nav) !important; }
    .pwa-mobile-nav-active { background-color: var(--pwa-mobile-nav-active) !important; }
    .pwa-status-indicator { background-color: var(--pwa-status-indicator) !important; }
    .pwa-status-indicator-text { color: var(--pwa-status-indicator-text) !important; }
    
    /* Brand-specific gradients */
    .bg-gradient-to-br.from-blue-50.to-green-50, .brand-gradient {
      background: linear-gradient(to bottom right, var(--brand-primary-light), ${adjustOpacity(colors.secondary, 0.1)}) !important;
    }
    
    /* Brand logo background fallback */
    .brand-logo-bg {
      background-color: var(--brand-primary) !important;
    }
    
    /* Dynamic button styles */
    .btn-brand-primary {
      background-color: var(--brand-primary) !important;
      color: white !important;
    }
    .btn-brand-primary:hover {
      background-color: var(--brand-primary-hover) !important;
    }
    
    /* Enhanced PWA component button styles */
    .btn-pwa-install {
      background-color: var(--pwa-install-prompt) !important;
      color: white !important;
      border: none !important;
    }
    .btn-pwa-install:hover {
      background-color: var(--pwa-install-prompt-hover) !important;
    }
    
    .btn-pwa-network {
      background-color: var(--pwa-status-indicator) !important;
      color: var(--pwa-status-indicator-text) !important;
    }
  `;
  
  document.head.appendChild(style);
};

// Helper function to adjust color brightness
const adjustBrightness = (color: string, percent: number): string => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, (num >> 8 & 0x00FF) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
  
  return '#' + ((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1);
};

// Helper function to adjust color opacity
const adjustOpacity = (color: string, opacity: number): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Helper function to generate a complete theme from primary color
const generateThemeFromPrimary = (primaryColor: string): BrandColors => {
  return {
    primary: primaryColor,
    primaryHover: adjustBrightness(primaryColor, -15),
    primaryLight: adjustOpacity(primaryColor, 0.15),
    primaryDark: adjustBrightness(primaryColor, -30),
    secondary: '#10b981', // Default green
    secondaryHover: adjustBrightness('#10b981', -15),
    secondaryLight: adjustOpacity('#10b981', 0.15),
    accent: '#f59e0b', // Default amber
    accentHover: adjustBrightness('#f59e0b', -15),
    background: '#ffffff',
    text: '#1f2937',
    textSecondary: '#6b7280',
    textMuted: '#9ca3af',
    success: '#22c55e',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6'
  };
};

// Helper function to get color for network status
const getNetworkStatusColor = (status: 'online' | 'slow' | 'offline', theme: BrandColors): string => {
  const pwaTheme = createPWAComponentTheme(theme);
  switch (status) {
    case 'online': return pwaTheme.networkOnline;
    case 'slow': return pwaTheme.networkSlow;
    case 'offline': return pwaTheme.networkOffline;
    default: return pwaTheme.networkOffline;
  }
};

// Export all functions and types
export {
  adjustBrightness,
  adjustOpacity,
  generateThemeFromPrimary,
  getNetworkStatusColor,
  createPWAComponentTheme
};